import React, {useCallback, useEffect, useMemo, useState} from "react";
import {axiosInstance} from "../../Api/AxiosConfig";
import {PresalesListTable} from "./PresalesListTable";
import {Waiting} from "../../Components/System/Waiting";
import {CarFilter, InitControlValueData} from "./CarFilter";
import {useGlobalDataContext} from "../../GlobalState/GlobalDataProvider";
import {StatusAndCheckoutButton} from "./StatusAndCheckoutButton";
import {findMinMaxDamageAmount, findMinMaxKm, findMinMaxPrices} from "../../Hooks/UseGenericFunctions";
import {filterAndSortList} from "../../Hooks/UseCarFilter";
import {CarViewEnum} from "../../Enums/CarViewEnum";
import {Showroom} from "../Showroom/Showroom";
import {useCarListParameters} from "../../Hooks/useCarListParameters";
import {useSearchParams} from "react-router-dom";
import {useDisclosure} from "@mantine/hooks";
import {registerUserActivity} from "../../Api/StatService";
import {DirectionEnum} from "../../Enums/DirectionEnum";
import {TabButton} from "../../Components/System/Buttons/TabButton";
import {ListTypeEnum} from "../../Enums/ListTypeEnum";

export const Presales = ({fnUpdateGlobalData, pushUpdate}) => {
    const [carzelleCars, setCarzelleCars] = useState();
    const [carzelleCarsMakes, setCarzelleCarsMakes] = useState();
    const [carzelleCarsFuels, setCarzelleCarsFuels] = useState();
    const [carzelleCarsTypes, setCarzelleCarsTypes] = useState();
    const [partnerCars, setPartnerCars] = useState();
    const [partnerCarsMakes, setPartnerCarsMakes] = useState();
    const [partnerCarsFuels, setPartnerCarsFuels] = useState();
    const [partnerCarsTypes, setPartnerCarsTypes] = useState();
    const [equipmentIcons, setEquipmentIcons] = useState();
    const [sortByField, setSortByField] = useState('designation');
    const [sortByFieldAscending, setSortByFieldAscending] = useState(true);
    const [filterData, setFilterData] = useState(InitControlValueData);
    const [updateCarList, setUpdateCarList] = useState(false);
    const [activeCar, setActiveCar] = useState();
    const [, setShowDetails] = useState(false);
    const {globalData} = useGlobalDataContext();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const isShowBasket = searchParams.get('showBasket') === 'false';
    const {listType, viewType, carHash, setCarListParameters} = useCarListParameters();
    const [opened, {open, close}] = useDisclosure(false);
    const [currentCarIndex, setCurrentCarIndex] = useState(null);
    const [previousUpdateStatus, setPreviousUpdateStatus] = useState(false);

    const fetchCarList = (listTypeId = 1, setListDataVariable) => {
        axiosInstance
            .get('/shop/buy-list', {
                params: {
                    listTypeId: listTypeId,
                }
            })

            .then((response) => {
                fnUpdateGlobalData();
                if (listTypeId === 1) {
                    setCarzelleCarsMakes(response.data.makes);
                    setCarzelleCarsFuels(response.data.fuel);
                    setCarzelleCarsTypes(response.data.carTypes);
                }
                if (listTypeId === 2) {
                    setPartnerCarsMakes(response.data.makes);
                    setPartnerCarsFuels(response.data.fuel);
                    setPartnerCarsTypes(response.data.carTypes);
                }
                setEquipmentIcons(response.data.equipmentBase);
                setListDataVariable(response.data.list);
                setCurrentCarIndex(0);
                registerUserActivity(3, 'Fetched Presales list');
            })
            .catch((error) => {
                console.error("Error fetching data:", error);
            });
    }

    useEffect(() => {
        let update = false;

        if (previousUpdateStatus !== updateCarList) {
            setPreviousUpdateStatus(updateCarList);
            update = true;
        }

        if ((!carzelleCars || update) && listType === ListTypeEnum.carzelle) {
            fetchCarList(1, setCarzelleCars);
        }

        if ((!partnerCars || update) && listType === ListTypeEnum.partner || update) {
            fetchCarList(2, setPartnerCars);
        }
    }, [updateCarList, pushUpdate, listType]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!globalData?.carUpdateCounter) return;
        setUpdateCarList(!updateCarList);
    }, [globalData?.carUpdateCounter]);

    useEffect(() => {
        if (carHash && carzelleCars) {
            const found = carzelleCars.find((car) => car.hash === carHash);
            if (found) {
                setActiveCar(found);
            }
        }
    }, [carzelleCars, carHash]);

    useEffect(() => {
        setShowDetails(true);
    }, [carHash]);

    const filteredAndSortedCars = useMemo(() => {
        if (listType === ListTypeEnum.carzelle) {
            return filterAndSortList(carzelleCars || [], filterData, sortByField, sortByFieldAscending);
        }
        if (listType === ListTypeEnum.partner) {
            return filterAndSortList(partnerCars || [], filterData, sortByField, sortByFieldAscending);
        }
    }, [carzelleCars, partnerCars, filterData, sortByField, sortByFieldAscending, listType]);

    const animateBasketIcon = () => {
        const element = document.getElementById('shopping_bag');  // Get the div element

        element.classList.add('animate-bounce');
        element.classList.remove('bg-purchase-color')
        element.classList.add('bg-sky-500');

        setTimeout(() => {
            element.classList.remove('bg-sky-500');
            element.classList.remove('animate-bounce');
            element.classList.add('bg-purchase-color');
        }, 1650);
    }

    useEffect(() => {
        setLoading(false);
    }, [listType]);

    const handleCarClicked = (car) => {
        if (car) {
            setCarListParameters({carhash: car.hash});
            setActiveCar(car);
        } else {
            setCarListParameters({carhash: null});
            setActiveCar(null);
        }
    };

    useEffect(() => {
        if (activeCar && carzelleCars?.length) {
            const index = carzelleCars.findIndex(car => car.hash === activeCar.hash);
            setCurrentCarIndex(index);
            open();
        } else {
            close();
        }
    }, [activeCar]);

    const changeCar = useCallback((direction) => {
        if (carzelleCars && currentCarIndex !== null && opened) {
            const count = carzelleCars.length;
            const newIndex =
                direction === DirectionEnum.next
                    ? (currentCarIndex + 1) % count
                    : (currentCarIndex - 1 + count) % count;

            handleCarClicked(carzelleCars[newIndex]);
        }
    }, [currentCarIndex, carzelleCars, opened, handleCarClicked]);

    const handleKeyDown = useCallback((e) => {
        if (!opened || currentCarIndex === null) return;

        if (e.key === 'ArrowRight') {
            changeCar(DirectionEnum.next);
        } else if (e.key === 'ArrowLeft') {
            changeCar(DirectionEnum.previous);
        }
    }, [opened, currentCarIndex, carzelleCars]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    const handleListTypeChange = (listInfo) => {
        setCarListParameters({listType: listInfo.identifier});
        setCarListParameters({viewType: listInfo.view});
        setFilterData(InitControlValueData);
    };

    if (((carzelleCars && listType === ListTypeEnum.carzelle) || (partnerCars && listType === ListTypeEnum.partner)) && globalData && !loading) {
        const {minPrice, maxPrice} = findMinMaxPrices(listType === ListTypeEnum.carzelle ? carzelleCars : partnerCars);
        const {minKm, maxKm} = findMinMaxKm(listType === ListTypeEnum.carzelle ? carzelleCars : partnerCars);
        const {minDamageAmount, maxDamageAmount} = findMinMaxDamageAmount(listType === ListTypeEnum.carzelle ? carzelleCars : partnerCars);

        return (
            <>
                <div className="lg:hidden flex items-center justify-center mt-12 p-8 text-2xl">
                    <div className="text-center">The presales function can only be used on desktop computers</div>
                </div>

                {/*<ScreenSizeIndicator/>*/}
                <div className="hidden lg:block">
                    <div className="mx-4 flex" style={{height: '92vh'}}>
                        <div className="relative mr-1 2xl:mr-4 h-[92vh] w-[100vw]">
                            <div className="absolute top-[10px] right-4 z-20">
                                <StatusAndCheckoutButton cars={globalData.basket.basket_lines} sum={globalData.basket.basket_totals.amount} currency={globalData.basket.basket_totals.currency} showBasket={isShowBasket} fnUpdate={() => setUpdateCarList(!updateCarList)}/>
                            </div>
                            <div className="bg-gray-300 rounded-lg p-4 h-[60px] flex items-center text-gray-500 mt-2">
                                <TabButton identifier={ListTypeEnum.carzelle} onClick={handleListTypeChange} label="Confirmed & Fast Delivery" number={globalData.globals.salesListCount.carzelle} className="mr-4" active={listType === ListTypeEnum.carzelle}/>
                                <TabButton identifier={ListTypeEnum.partner} onClick={handleListTypeChange} label="Partner Listings" number={globalData.globals.salesListCount.presales} active={listType === ListTypeEnum.partner}/>
                            </div>

                            {(viewType === CarViewEnum.list && listType === ListTypeEnum.carzelle) &&
                                <div className={`mt-2`}>
                                    <div className={`overflow-auto h-[92vh]`}>
                                        <PresalesListTable cars={filteredAndSortedCars} hidePrices={filterData.hide_prices} equipmentIcons={equipmentIcons} activeCarId={carHash} activeSortField={sortByField} activeSortAscending={sortByFieldAscending} fnSetSortByField={setSortByField} fnSetSortByFieldOrder={setSortByFieldAscending} fnUpdateCarList={() => {
                                            setUpdateCarList(!updateCarList);
                                            animateBasketIcon();
                                        }} fnSetActiveCar={handleCarClicked}
                                        />
                                    </div>
                                </div>
                            }

                            {(viewType === CarViewEnum.list && listType === ListTypeEnum.partner) &&
                                <div className={`mt-2`}>
                                    <div className={`overflow-auto h-[92vh]`}>
                                        <PresalesListTable cars={filteredAndSortedCars} hidePrices={filterData.hide_prices} equipmentIcons={equipmentIcons} activeCarId={carHash} activeSortField={sortByField} activeSortAscending={sortByFieldAscending} fnSetSortByField={setSortByField} fnSetSortByFieldOrder={setSortByFieldAscending} fnUpdateCarList={() => {
                                            setUpdateCarList(!updateCarList);
                                            animateBasketIcon();
                                        }} fnSetActiveCar={handleCarClicked}
                                        />
                                    </div>
                                </div>
                            }

                            <div className={`${viewType === CarViewEnum.cards && listType === ListTypeEnum.carzelle ? 'block' : 'hidden'}`}>
                                <Showroom selected={listType === ListTypeEnum.cards} selectedCar={activeCar} hidePrices={filterData.hide_prices} changeCar={changeCar} opened={opened} open={open} close={close} cars={filteredAndSortedCars} equipmentBaseIcons={equipmentIcons} fnCardClicked={handleCarClicked} fnUpdateData={() => setUpdateCarList(!updateCarList)}/>
                            </div>
                            <div className={`${viewType === CarViewEnum.cards && listType === ListTypeEnum.partner ? 'block' : 'hidden'}`}>
                                <Showroom selected={listType === ListTypeEnum.cards} selectedCar={activeCar} hidePrices={filterData.hide_prices} changeCar={changeCar} opened={opened} open={open} close={close} cars={filteredAndSortedCars} equipmentBaseIcons={equipmentIcons} fnCardClicked={handleCarClicked} fnUpdateData={() => setUpdateCarList(!updateCarList)}/>
                            </div>
                        </div>
                        <div className="min-h-[100vh]">
                            <div className="pb-4 h-full min-w-[170px]">
                                {(carzelleCarsTypes && listType === ListTypeEnum.carzelle) && <CarFilter makes={carzelleCarsMakes} fuels={carzelleCarsFuels} carTypes={carzelleCarsTypes} filterData={filterData} fnFilterChange={(filterValues) => setFilterData(filterValues)} minPrice={minPrice} maxPrice={maxPrice} minKm={minKm} maxKm={maxKm} minDamageAmount={minDamageAmount} maxDamageAmount={maxDamageAmount}/>}
                                {(partnerCarsTypes && listType === ListTypeEnum.partner) && <CarFilter makes={partnerCarsMakes} fuels={partnerCarsFuels} carTypes={partnerCarsTypes} filterData={filterData} fnFilterChange={(filterValues) => setFilterData(filterValues)} minPrice={minPrice} maxPrice={maxPrice} minKm={minKm} maxKm={maxKm} minDamageAmount={minDamageAmount} maxDamageAmount={maxDamageAmount}/>}
                            </div>
                        </div>
                    </div>
                </div>
            </>
        )
    } else {
        return (
            <Waiting/>
        );
    }
}